import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { methodologyService, Methodology, GuidedMethodology } from "../../lib/firebase";
import { toast } from "../../components/ui/use-toast";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const ADMIN_EMAIL = "admin@rawpa.com";

export const AllMethodologies = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [straightUpMethodologies, setStraightUpMethodologies] = useState<Methodology[]>([]);
  const [guidedMethodologies, setGuidedMethodologies] = useState<GuidedMethodology[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
      }
    });

    // Load all methodologies
    const loadAllMethodologies = async () => {
      try {
        setLoading(true);
        
        // Load straight-up methodologies
        const straightUp = await methodologyService.getMethodologies();
        setStraightUpMethodologies(straightUp);
        
        // Load guided methodologies
        const guided = await methodologyService.getGuidedMethodologies();
        setGuidedMethodologies(guided);
        
        setLoading(false);
      } catch (error) {
        console.error("Error loading methodologies:", error);
        setError("Failed to load methodologies. Please try again.");
        setLoading(false);
      }
    };

    loadAllMethodologies();
    return () => unsubscribe();
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/aAyuiOcvMq');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleDeleteStraightUpMethodology = async (methodologyId: string) => {
    if (window.confirm("Are you sure you want to delete this methodology? This action cannot be undone.")) {
      try {
        const success = await methodologyService.deleteMethodology(methodologyId);
        if (success) {
          toast({
            title: "Methodology Deleted",
            description: "The methodology has been deleted successfully.",
          });
          setStraightUpMethodologies(prev => prev.filter(m => m.id !== methodologyId));
        } else {
          toast({
            title: "Error",
            description: "Failed to delete methodology. Please try again.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error deleting methodology:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleDeleteGuidedMethodology = async (methodologyId: string) => {
    if (window.confirm("Are you sure you want to delete this guided methodology? This action cannot be undone.")) {
      try {
        const success = await methodologyService.deleteGuidedMethodology(methodologyId);
        if (success) {
          toast({
            title: "Guided Methodology Deleted",
            description: "The guided methodology has been deleted successfully.",
          });
          setGuidedMethodologies(prev => prev.filter(m => m.id !== methodologyId));
        } else {
          toast({
            title: "Error",
            description: "Failed to delete guided methodology. Please try again.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error deleting guided methodology:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  // Filter methodologies based on search query
  const filteredStraightUpMethodologies = straightUpMethodologies.filter(m => 
    m.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    (m.description && m.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (m.keywords && m.keywords.some(k => k.toLowerCase().includes(searchQuery.toLowerCase())))
  );

  const filteredGuidedMethodologies = guidedMethodologies.filter(m => 
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    (m.description && m.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (m.keywords && m.keywords.some(k => k.toLowerCase().includes(searchQuery.toLowerCase())))
  );

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/aAyuiOcvMq/dashboard')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA Admin</span>
            </div>
            <div 
              onClick={handleSignOut}
              className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
            >
              <img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" />
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
                All Methodologies
              </h1>
              <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
                View, edit, or delete all methodologies in one place
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                className="h-10 px-4 bg-gray-100 text-black rounded-lg hover:bg-gray-200 transition-colors"
                onClick={() => navigate('/aAyuiOcvMq/dashboard')}
              >
                Back to Dashboard
              </Button>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p>{error}</p>
            </div>
          )}

          {/* Search and filter */}
          <div className="mb-6">
            <div className="flex gap-4">
              <Input
                placeholder="Search methodologies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1"
              />
            </div>
          </div>

          {/* Tabs for filtering */}
          <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="straight-up">Straight-up</TabsTrigger>
              <TabsTrigger value="guided">Guided</TabsTrigger>
            </TabsList>
          </Tabs>

          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading methodologies...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Straight-up Methodologies */}
              {(activeTab === "all" || activeTab === "straight-up") && (
                <div>
                  {activeTab === "all" && <h2 className="font-['Space_Grotesk'] font-bold text-xl text-black mb-4">Straight-up Methodologies</h2>}
                  
                  {filteredStraightUpMethodologies.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-gray-500">No straight-up methodologies found.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {filteredStraightUpMethodologies.map((methodology) => (
                        <Card key={methodology.id} className="overflow-hidden">
                          <CardHeader className="bg-gray-50 pb-2">
                            <div className="flex justify-between items-start">
                              <CardTitle className="text-lg">{methodology.title}</CardTitle>
                              <Badge variant="outline">Straight-up</Badge>
                            </div>
                            {methodology.description && (
                              <CardDescription className="mt-1">
                                {methodology.description}
                              </CardDescription>
                            )}
                          </CardHeader>
                          <CardContent className="pt-4">
                            <div className="flex justify-between items-center">
                              <div className="text-sm text-gray-500">
                                <span>{methodology.subMethodologies.length} sub-methodologies</span>
                                {methodology.keywords && methodology.keywords.length > 0 && (
                                  <span className="ml-2">• {methodology.keywords.length} keywords</span>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => navigate(`/aAyuiOcvMq/methodologies/${methodology.id}`)}
                                >
                                  View
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive" 
                                  onClick={() => handleDeleteStraightUpMethodology(methodology.id)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Guided Methodologies */}
              {(activeTab === "all" || activeTab === "guided") && (
                <div>
                  {activeTab === "all" && <h2 className="font-['Space_Grotesk'] font-bold text-xl text-black mb-4 mt-8">Guided Methodologies</h2>}
                  
                  {filteredGuidedMethodologies.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-gray-500">No guided methodologies found.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {filteredGuidedMethodologies.map((methodology) => (
                        <Card key={methodology.id} className="overflow-hidden">
                          <CardHeader className="bg-gray-50 pb-2">
                            <div className="flex justify-between items-start">
                              <CardTitle className="text-lg">{methodology.name}</CardTitle>
                              <div className="flex gap-2">
                                <Badge variant="outline">Guided</Badge>
                                <Badge variant={
                                  methodology.status === 'Active' ? 'success' :
                                  methodology.status === 'Draft' ? 'warning' : 'secondary'
                                }>
                                  {methodology.status}
                                </Badge>
                              </div>
                            </div>
                            {methodology.description && (
                              <CardDescription className="mt-1">
                                {methodology.description}
                              </CardDescription>
                            )}
                          </CardHeader>
                          <CardContent className="pt-4">
                            <div className="flex justify-between items-center">
                              <div className="text-sm text-gray-500">
                                <span>{methodology.steps.length} steps</span>
                                {methodology.keywords && methodology.keywords.length > 0 && (
                                  <span className="ml-2">• {methodology.keywords.length} keywords</span>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => navigate(`/aAyuiOcvMq/guided-methodologies/${methodology.id}`)}
                                >
                                  View
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive" 
                                  onClick={() => handleDeleteGuidedMethodology(methodology.id)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 